import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { CloseCircleOutlined, LoadingOutlined, WarningOutlined, ClockCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import makeApiRequest from '@apiCalls/makeApiRequest';
import { API_ENDPOINTS, MY_PODS_URL } from '@constants/apiEndpoints';
import { useNotification } from '@providers/NotificationProviders';
import type apiRequestConfig from '@interfaces/apiEndpoints.interface';
import * as styles from '@styles/militaryVerification.module.scss';
import podsLogo from '@assets/pods_logo_1.png';

const MilitaryCallback: React.FC = () => {
    const navigate = useNavigate();
    const openNotification = useNotification();
    const processedRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const [countdown, setCountdown] = useState(15);

    useEffect(() => {
        if (processedRef.current) return;
        processedRef.current = true;

        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const errorParam = urlParams.get('error');

        if (errorParam) {
            setError(errorParam);
            return;
        }

        if (code && state) {
            // Call backend to complete verification using the code
            const apiConfig = API_ENDPOINTS.COMPLETE_MILITARY_VERIFICATION as unknown as ((param: string) => apiRequestConfig);
            const query = `?code=${encodeURIComponent(code!)}&state=${encodeURIComponent(state!)}`;

            makeApiRequest(
                {}, // Empty body
                {
                    url: `${(API_ENDPOINTS.COMPLETE_MILITARY_VERIFICATION as any).url('')}${query}`,
                    method: 'POST'
                } as apiRequestConfig,
                ''
            )
                .then((res) => {
                    if (res.status === 200) {
                        openNotification('Verification successful!', 'Success');
                        const verificationId = res.data?.verificationId || state;
                        navigate(`/military/verify/${verificationId}`);
                    } else {
                        // Handle cases where status is not 200 but no exception thrown (if axios/fetch wrapper behaves this way)
                        setError(res.data?.message || 'Verification failed');
                    }
                })
                .catch((err) => {
                    const errorMsg = err.response?.data?.message || 'Verification failed';
                    setError(errorMsg);
                });
        } else if (code || state) {
            setError('Missing required parameters for verification.');
        } else {
            navigate('/military/reauthorize', { replace: true });
        }

    }, [navigate, openNotification]);

    useEffect(() => {
        const isPending = error?.toLowerCase().includes('pending');

        if (isPending && countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (isPending && countdown === 0) {
            window.location.href = MY_PODS_URL;
        }
    }, [error, countdown]);

    if (error) {
        const isPending = error.toLowerCase().includes('pending');

        return (
            <div className={styles.loginBody}>
                <div className={styles.logoWeb}>
                    <img src={podsLogo as any} alt="PODS Logo" />
                </div>
                <div className={styles.formLogin}>
                    <div className={styles.errorCard} style={{ boxShadow: 'none', padding: 0 }}>
                        {isPending ? (
                            <>
                                <div className={styles.reviewIcon}>
                                    <ClockCircleOutlined />
                                </div>
                                <h2 className={styles.reviewTitle}>
                                    Your GovX Verification is <br /> Under Review
                                </h2>
                                <div className={styles.reviewSubtitle}>
                                    You will receive your approval email from GovX within the next 24 hours.
                                    In the meantime, please login to MyPODS to manage your order.
                                </div>
                                <Button
                                    type="primary"
                                    className={styles.manageOrdersBtn}
                                    onClick={() => window.location.href = MY_PODS_URL}
                                >
                                    Manage order in MyPODS
                                </Button>
                                <div className={styles.redirectText}>
                                    You will be automatically redirected to MyPODS in {countdown} seconds.
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.errorIcon}>
                                    <CloseCircleOutlined />
                                </div>
                                <h2 className={styles.errorTitle}>Verification Failed</h2>
                                <div style={{ marginBottom: '24px', textAlign: 'center', width: '100%' }}>
                                    <div className={styles.errorDetailsBox}>
                                        <div className={styles.errorDetailsTitle}>
                                            <WarningOutlined className={styles.errorDetailsIcon} />
                                            Error Details
                                        </div>
                                        <div className={styles.errorMessage}>{error}</div>
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#666', marginTop: '16px' }}>
                                        To manage your orders visit <a href={MY_PODS_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#297ed1', fontWeight: 600 }}>mypods.com</a>
                                    </div>
                                </div>
                            </>
                        )}

                        {!isPending && (
                            <div className={styles.commonReasonsBox}>
                                <div className={styles.commonReasonsTitle}>Common reasons:</div>
                                <ul className={styles.commonReasonsList}>
                                    <li>Email mismatch (authenticated with different email)</li>
                                    <li>Authentication session timed out</li>
                                    <li>Technical error with the verification service</li>
                                </ul>
                            </div>
                        )}

                        {!isPending && (
                            <div style={{ display: 'flex', gap: '12px', marginTop: '8px', justifyContent: 'center' }}>
                                <Button
                                    type="default"
                                    className={styles.contactSupportBtn}
                                    onClick={() => window.open('https://www.pods.com/contact-us', '_blank')}
                                >
                                    Contact Support
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.loginBody}>
            <div className={styles.logoWeb}>
                <img src={podsLogo as any} alt="PODS Logo" />
            </div>
            <div className={styles.formLogin}>
                <LoadingOutlined className={styles.loadingSpinner} spin />
                <div className={styles.loadingText}>Completing verification...</div>
                <div className={styles.loadingSubText}>Please wait while we complete your verification.</div>
            </div>
        </div>
    );
};

export default MilitaryCallback;
